'use client';

import { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { filterXSS } from 'xss';
import { Text } from '@lmn/design-system';
import { useTranslation } from '@/app/components/translations/Translation';
import { getAvailableCancellationMealPlans } from '@/app/components/hotel-detail/rooms/RoomPanelCommon';
import { EvoRoomCancellationOptions } from '@/app/components/hotel-detail/evo/rooms/EvoRoomCancellationOptions';
import { EvoRoomCancellationInformation } from '@/app/components/hotel-detail/evo/rooms/EvoRoomCancellationInformation';
import { getPromotionGroupMealPlans } from '@/app/components/hotel-detail/evo/rooms/promotionGroup';
import { Icon } from '@/app/common/icons/Icon';

type EvoRoomCancellationTypeSelectionProps = {
  room: RoomType;
  selectedMealPlan: MealPlanType;
  onSelectMealPlan: (selectedMealPlan: MealPlanType) => void;
  // The committed selection the option set is derived from, when `selectedMealPlan` is an uncommitted
  // draft (the cancellation modal). Defaults to `selectedMealPlan`. See EvoRoomCancellationOptions.
  anchorMealPlan?: MealPlanType;
  // Wrap the cancellation options in a bordered box (the "Learn more" toggle stays outside it).
  bordered?: boolean;
};

// Native button so the disclosure gets keyboard semantics (Enter/Space) and a focus-visible ring for free.
const LearnMoreToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
  color: inherit;
`;

// Cancellation-type selection block for the EVO flow: the radio group of cancellation options plus an
// expandable "Cancellation options" policy explainer. Shared by EvoCancellationTypeSelectionModal and the
// mobile room-details panel so the two stay in sync.
export function EvoRoomCancellationTypeSelection(props: EvoRoomCancellationTypeSelectionProps) {
  const { room, selectedMealPlan, onSelectMealPlan, anchorMealPlan = selectedMealPlan, bordered = false } = props;
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  // Fees for the anchor's (board class × promotion group) only, but across every cancellation option it
  // offers (refundable + non-refundable) — the same candidate set as the radio group above (MPH-652), so
  // the explainer never lists fees for a rate the options don't offer. Derived from the committed
  // selection (anchorMealPlan) so it doesn't change as the user toggles between cancellation options.
  const availableMealPlans = useMemo(
    () =>
      getAvailableCancellationMealPlans(
        getPromotionGroupMealPlans(room.configurations, anchorMealPlan),
        anchorMealPlan
      ),
    [room.configurations, anchorMealPlan]
  );

  const cancellationOptions = (
    <EvoRoomCancellationOptions
      room={room}
      selectedMealPlan={selectedMealPlan}
      onSelectMealPlan={onSelectMealPlan}
      anchorMealPlan={anchorMealPlan}
    />
  );

  return (
    <div
      css={`
        display: flex;
        flex-direction: column;
        gap: 16px;
      `}
    >
      {bordered ? (
        <div
          css={css`
            border: 1px solid ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
            border-radius: ${({ theme }) => theme.kallisto.border.radius.radius200};
            padding: ${({ theme }) => `${theme.kallisto.space.space100} ${theme.kallisto.space.space200}`};
          `}
        >
          {cancellationOptions}
        </div>
      ) : (
        cancellationOptions
      )}

      <LearnMoreToggle
        type="button"
        aria-expanded={expanded}
        aria-controls="evo-cancellation-options-info"
        onClick={() => setExpanded((prev) => !prev)}
      >
        {/* Copy embeds a bare <span> around "Cancellation options"; underline it via the descendant selector. */}
        <Text
          variant="BodySmall"
          css={css`
            span {
              text-decoration: underline;
            }
          `}
          dangerouslySetInnerHTML={{
            __html: filterXSS(t('vacation.select.cc.rooms.cancellation_options.learn_more')),
          }}
        />
        <Icon
          category="Arrows"
          name="icon-chevron-right"
          size={12}
          footprint={20}
          color="iconSecondary"
          alt=""
          css={`
            transform: rotate(${expanded ? '-90deg' : '90deg'});
          `}
        />
      </LearnMoreToggle>

      {expanded && (
        <div
          id="evo-cancellation-options-info"
          css={css`
            display: flex;
            flex-direction: column;
            gap: 16px;
          `}
        >
          <div
            css={css`
              height: 1px;
              background-color: ${({ theme }) => theme.kallisto.color.border.static.borderDisabled};
            `}
          />
          {/* Scoped to the selected board class and promotion group, but showing all its cancellation
              options — unlike the room-card / trip-overview modal, which lists the fees for every meal
              plan of the room. */}
          <EvoRoomCancellationInformation mealPlans={availableMealPlans} />
        </div>
      )}
    </div>
  );
}
